import { useState } from 'react';

import EmailIcon from 'icons/email.svg?react';
import { toast } from 'material-react-toastify';

import Popup from '@/components/Popup/PopUp/PopUp';
import Button from '@/components/shared/Button/Button';
import { Select } from '@/components/shared/Select/Select';
import { Text } from '@/components/shared/Text/Text';
import { hubApi, withAuthHeader } from '@/lib/backend/api';
import { getAuthToken } from '@/lib/firebase/token';
import { useHubStore } from '@/lib/store/hubStore';
import { CreateComplimentarySubscriptionHubRequest } from '@addmile/backend-client';
import { useMutation } from '@tanstack/react-query';

const useUpsertSubscription = (userId: string) => {
  return useMutation({
    mutationFn: async (data: CreateComplimentarySubscriptionHubRequest) => {
      const token = await getAuthToken();
      return hubApi.createComplimentarySubscriptionHub(userId, data, withAuthHeader(token));
    },
  });
};

interface UpsertSubscriptionModalProps {
  isPopupOpen: boolean;
  closePopup: () => void;
}

const UpsertSubscriptionModal = ({ isPopupOpen, closePopup }: UpsertSubscriptionModalProps) => {
  const user = useHubStore((state) => state.searchUserData);
  const [expirationDate, setExpirationDate] = useState<string>('');
  const updateExpirationDateMutation = useUpsertSubscription(user?.id);

  const handleChangeName = async () => {
    if (!user?.id) {
      toast.error('User ID is missing');
      return;
    }

    const newExpirationDate = calculateExpirationDate(expirationDate);

    updateExpirationDateMutation.mutate(
      {
        expiresAt: newExpirationDate,
      },
      {
        onSuccess: () => {
          closePopup();
          toast.success(`User's expiration date was successfully changed`);
        },
        onError: (error) => {
          closePopup();
          toast.error(error.message);
        },
      },
    );
  };

  const options = [
    { label: 'Select', value: '', disabled: true },
    { label: '+ 2 weeks', value: '2w' },
    { label: '+ 1 month', value: '1m' },
    { label: '+ 2 months', value: '2m' },
    { label: '+ 3 months', value: '3m' },
    { label: '+ 6 months', value: '6m' },
  ];

  const calculateExpirationDate = (value: string) => {
    const date = new Date();

    const unit: 'w' | 'm' = value.slice(-1) as 'w' | 'm';
    const amount = parseInt(value.slice(0, -1), 10);

    if (unit === 'w') date.setDate(date.getDate() + amount * 7);
    else if (unit === 'm') date.setMonth(date.getMonth() + amount);

    return date.toISOString();
  };

  return (
    <Popup isOpen={isPopupOpen} handleClose={closePopup}>
      <div className="absolute top-[20px] left-[12px] rounded-lg bg-[#F2F2F2] p-2">
        <EmailIcon />
      </div>
      <div className="max-w-[340px] ml-12">
        <Text.Bigger className="mb-2" weight="bold">
          Add Complimentary Subscription
        </Text.Bigger>
        <Text.Regular>
          Please select the expiration date for complimentary subscription:
        </Text.Regular>
        <Select
          className="w-full my-4"
          options={options}
          value={expirationDate}
          onChange={(value) => setExpirationDate(value || '')}
        />

        <div className="flex justify-between items-center gap-2">
          <Button
            label={updateExpirationDateMutation.isPending ? 'Adding...' : 'Add Subscription'}
            size="s"
            className="!w-44"
            onClick={handleChangeName}
            disabled={updateExpirationDateMutation.isPending || !expirationDate}
            prefixIcon={<img alt={'check'} src={'/icons/check.svg'} />}
          />
          <Button
            prefixIcon={<img alt={'close'} src={'/icons/close_outline.svg'} />}
            label="Cancel"
            variant="transparent"
            onClick={closePopup}
            className="!w-28"
            size="s"
            disabled={updateExpirationDateMutation.isPending}
          />
        </div>
      </div>
    </Popup>
  );
};

export default UpsertSubscriptionModal;
